// nodeCreation.js
// Helper functions to create nodes with automatic blockId generation

import {generateUUID} from "./utils";

/**
 * Create a block node with automatic blockId generation
 * Use this instead of nodeType.create() to ensure blockIds are always set
 *
 * @param {NodeType} nodeType - The ProseMirror node type
 * @param {Object} attrs - Node attributes (blockId will be auto-generated if not provided)
 * @param {Fragment|Node|Array<Node>} content - Node content
 * @param {Array<Mark>} marks - Node marks
 * @returns {Node} Created node with blockId
 *
 * @example
 * // Instead of: schema.nodes.paragraph.create()
 * // Use: createBlockNode(schema.nodes.paragraph)
 *
 * // Instead of: schema.nodes.paragraph.create({classificationLevel: 'S'})
 * // Use: createBlockNode(schema.nodes.paragraph, {classificationLevel: 'S'})
 */
export function createBlockNode(nodeType, attrs = null, content = null, marks = null) {
    // Check if this node type has blockId attribute
    const hasBlockId = nodeType.spec?.attrs?.blockId !== undefined;

    // If it has blockId and attrs don't already provide one, generate it
    const finalAttrs = hasBlockId && (!attrs || !attrs.blockId)
        ? {...attrs, blockId: generateUUID()}
        : attrs;

    return nodeType.create(finalAttrs, content, marks);
}

/**
 * Create a paragraph node with automatic blockId
 * Convenience wrapper for the most common case
 *
 * @param {Schema} schema - ProseMirror schema
 * @param {Object} attrs - Attributes
 * @param {Fragment|Node|Array<Node>} content - Content
 * @returns {Node} Paragraph node with blockId
 */
export function createParagraph(schema, attrs = null, content = null) {
    return createBlockNode(schema.nodes.paragraph, attrs, content);
}

/**
 * Create a heading node with automatic blockId
 *
 * @param {Schema} schema - ProseMirror schema
 * @param {number} level - Heading level (1-6)
 * @param {Object} extraAttrs - Additional attributes
 * @param {Fragment|Node|Array<Node>} content - Content
 * @returns {Node} Heading node with blockId
 */
export function createHeading(schema, level, extraAttrs = null, content = null) {
    const attrs = {...extraAttrs, level};
    return createBlockNode(schema.nodes.heading, attrs, content);
}

